import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaSearch,
  FaBullhorn,
  FaChartLine,
  FaPenFancy,
  FaShareAlt,
  FaCode,
  FaArrowRight,
  FaRocket,
  FaWhatsapp,
  FaCamera
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { Link } from 'react-router-dom';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [hoveredIndex, setHoveredIndex] = useState(null);

  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to enquire about your digital marketing services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const services = [
    {
      icon: FaBullhorn,
      title: 'Digital Advertising',
      description: 'Maximize ROI with precision-targeted campaigns that convert browsers into loyal customers.',
      color: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50',
      features: ['Google Ads', 'Social Ads', 'Retargeting'],
      stats: '4.5x ROI Average',
      emoji: '📢',
    },
    {
      icon: FaPenFancy,
      title: 'Content Marketing',
      description: 'Engage audiences with storytelling that resonates and content that converts like magic.',
      color: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50',
      features: ['Blog Writing', 'Copywriting', 'Video Content'],
      stats: '3x Engagement',
      emoji: '✍️',
    },
    {
      icon: FaShareAlt,
      title: 'Social Media',
      description: 'Build thriving communities and amplify your brand voice across all social platforms.',
      color: 'from-pink-500 to-rose-500',
      bgGradient: 'from-pink-50 to-rose-50',
      features: ['Strategy', 'Management', 'Influencer Marketing'],
      stats: '500k+ Reach',
      emoji: '🚀',
    },
    {
      icon: FaCode,
      title: 'Web Development',
      description: 'Build lightning-fast, stunning websites that captivate visitors and drive conversions.',
      color: 'from-indigo-500 to-purple-500',
      bgGradient: 'from-indigo-50 to-purple-50',
      features: ['Custom Design', 'E-commerce', 'Web Apps'],
      stats: '99.9% Uptime',
      emoji: '💻',
    },
    {
      icon: FaSearch,
      title: 'SEO Optimization',
      description: 'Dominate search rankings with cutting-edge SEO strategies that drive organic traffic and boost visibility.',
      color: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50',
      features: ['Keyword Research', 'On-Page SEO', 'Link Building'],
      stats: '250% Traffic Boost',
      emoji: '🎯',
    },
    {
      icon: FaChartLine,
      title: 'Analytics & Insights',
      description: 'Transform data into actionable insights with comprehensive tracking and performance analytics.',
      color: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50',
      features: ['Data Analysis', 'Reporting', 'Conversion Tracking'],
      stats: '98% Accuracy',
      emoji: '📊',
    },
    {
      icon: FaCamera,
      title: 'Photo & Video Shoot',
      description: 'Professional high-end photo and video production for brands, products, and corporate events.',
      color: 'from-orange-500 to-amber-500',
      bgGradient: 'from-orange-50 to-amber-50',
      features: ['Product & Brand Shoots', 'Reels & Ads', 'Professional Editing'],
      stats: '100+ Shoots Done',
      emoji: '📸',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="services" className="py-20 sm:py-24 lg:py-32 bg-gradient-to-b from-white via-slate-50 to-blue-50/40 relative overflow-hidden">
      {/* Enhanced Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.15, 0.25, 0.15],
            rotate: [0, 90, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/30 to-purple-300/30 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.3, 0.2],
            rotate: [90, 0, 90],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-40 -left-40 w-[700px] h-[700px] bg-gradient-to-tr from-pink-300/30 to-orange-300/30 rounded-full blur-[130px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.18, 0.28, 0.18],
            x: [-50, 50, -50],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-indigo-300/25 to-cyan-300/25 rounded-full blur-[110px]"
        />

        {/* Floating Sparkles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              opacity: [0, 0.5, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          >
            <HiSparkles className="text-purple-400" size={10 + Math.random() * 10} />
          </motion.div>
        ))}
      </div>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Enhanced Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200/50 text-purple-700 rounded-full text-sm font-bold mb-6 shadow-lg shadow-purple-100/50"
          >
            <FaRocket className="text-red-600" />
            <span>Our Premium Services</span>
            <HiSparkles className="text-orange-600" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight"
          >
            Digital Solutions That
            <br />
            <span className="relative inline-block mt-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-red-500 to-orange-600">
                Drive Real Results
              </span>
              <motion.span
                animate={{
                  scale: [1, 1.02, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 blur-xl opacity-30"
              />
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-medium"
          >
            Comprehensive digital marketing services tailored to your business needs,
            <span className="text-red-600 font-semibold"> delivering sustainable growth</span> in the digital landscape.
          </motion.p>
        </motion.div>

        {/* Clean & Professional Services Grid */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              whileHover={{ y: -8 }}
              className="group relative"
            >
              {/* Clean Card Container */}
              <div className={`relative bg-white rounded-2xl p-8 h-full transition-all duration-500 border-2 overflow-hidden ${hoveredIndex === index
                ? 'border-red-500 shadow-2xl shadow-red-500/20'
                : 'border-slate-100 shadow-lg hover:shadow-xl'
                }`}>

                {/* Top Accent Line */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${service.color} transform origin-left transition-transform duration-500 ${hoveredIndex === index ? 'scale-x-100' : 'scale-x-0'
                  }`} />

                {/* Corner Decoration */}
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${service.color} opacity-5 rounded-bl-full transition-all duration-500 ${hoveredIndex === index ? 'w-32 h-32 opacity-10' : ''
                  }`} />

                <div className="relative z-10">
                  {/* Icon Container - Clean Design */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg transition-all duration-300 ${hoveredIndex === index ? 'scale-110 shadow-xl' : ''
                      }`}>
                      <service.icon className="text-white text-xl" />

                      {/* Pulse Ring on Hover */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={hoveredIndex === index ? { scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] } : {}}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className={`absolute inset-0 rounded-xl bg-gradient-to-br ${service.color}`}
                      />
                    </div>

                    {/* Service Number Badge */}
                    <span className={`text-4xl font-black transition-all duration-300 ${hoveredIndex === index
                      ? 'text-red-500'
                      : 'text-slate-100'
                      }`}>
                      0{index + 1}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className={`text-xl font-bold mb-3 transition-all duration-300 ${hoveredIndex === index ? 'text-red-600' : 'text-slate-900'
                    }`}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-500 mb-5 leading-relaxed text-sm">
                    {service.description}
                  </p>

                  {/* Features - Clean List */}
                  <div className="space-y-2.5 mb-6">
                    {service.features.map((feature, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-3 transition-all duration-300 ${hoveredIndex === index ? 'translate-x-1' : ''
                          }`}
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.color}`} />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stats Chip */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6 transition-all duration-300 ${hoveredIndex === index
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-slate-50 border border-slate-100'
                    }`}>
                    <FaChartLine className={`text-xs ${hoveredIndex === index ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className={`text-xs font-semibold ${hoveredIndex === index ? 'text-red-600' : 'text-slate-600'
                      }`}>{service.stats}</span>
                  </div>

                  {/* Learn More - Clean Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <motion.a
                      href="#"
                      className={`inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300 ${hoveredIndex === index ? 'text-red-600' : 'text-slate-700'
                        }`}
                    >
                      Learn More
                      <motion.span
                        animate={hoveredIndex === index ? { x: [0, 4, 0] } : {}}
                        transition={{ duration: 0.8, repeat: Infinity }}
                      >
                        <FaArrowRight className="text-xs" />
                      </motion.span>
                    </motion.a>

                    {/* View Arrow Button */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${hoveredIndex === index
                        ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'bg-slate-100 text-slate-400'
                        }`}
                    >
                      <FaArrowRight className="text-xs" />
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16 lg:mt-20"
        >
          <div className="flex flex-wrap justify-center gap-4">
            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
              className="group relative px-10 py-5 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white font-bold rounded-full shadow-2xl shadow-red-500/40 overflow-hidden cursor-pointer"
            >
              {/* Animated Shimmer */}
              <motion.div
                animate={{ x: [-200, 200] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
              />

              <span className="relative flex items-center justify-center gap-3 text-lg">
                <FaRocket />
                Get Started
              </span>
            </motion.button>

            <Link to="/services">
              <motion.button
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-10 py-5 bg-white border-2 border-red-500 text-red-600 font-bold rounded-full shadow-xl hover:shadow-2xl overflow-hidden cursor-pointer"
              >
                <span className="relative flex items-center justify-center gap-3 text-lg">
                  View All Services
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
            </Link>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="mt-6 text-slate-600 font-medium"
          >
            Still have questions?
            <button onClick={openWhatsApp} className="text-red-600 font-bold ml-2 hover:underline cursor-pointer bg-transparent border-none">
              Talk to our experts →
            </button>
          </motion.p>
        </motion.div>
      </div>

      {/* WhatsApp Floating Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <motion.button
          onClick={openWhatsApp}
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          className="relative w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full shadow-2xl shadow-green-500/50 flex items-center justify-center cursor-pointer overflow-hidden group"
        >
          {/* Pulse Animation */}
          <motion.div
            animate={{ scale: [1, 1.5, 1.5], opacity: [0.5, 0, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-green-500 rounded-full"
          />

          {/* Icon */}
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="relative z-10"
          >
            <FaWhatsapp className="text-white text-3xl" />
          </motion.div>

          {/* Hover Shine Effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
            animate={{ x: [-100, 100] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
          />
        </motion.button>

        {/* Tooltip */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileHover={{ opacity: 1, x: 0 }}
          className="absolute right-20 top-1/2 -translate-y-1/2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap pointer-events-none"
        >
          Chat with us!
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-2 h-2 bg-slate-900 rotate-45" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Services;
